import {Badge} from '@/components/ui/badge/Badge'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {StyledPoint} from '@/features/projects/components/progress-status-badge/style'
import {Trans, useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {ProjectStatus, ProjectSuspendReason} from '../../types'
import {suspendReasonToLabel} from '../../utils'

export const ProjectSuspendedTooltip = ({
    projectStatus,
    suspensionDays,
    suspensionReason,
    suspensionStartDate,
    suspensionEndDate
}: {
    projectStatus: ProjectStatus
    suspensionDays: number | null
    suspensionStartDate?: Date | null
    suspensionReason?: ProjectSuspendReason | null
    suspensionEndDate?: Date | null
}) => {
    const {t} = useTranslation()

    if (projectStatus == 'closed') {
        return null
    }

    if (suspensionDays === null) {
        return (
            <Badge variant="danger" size="sm" shape="roundedRectangle">
                <AlertCircleIcon />
                {t('users:status_suspended')}
            </Badge>
        )
    }

    return (
        <Flexbox gap={2}>
            {isNaN(suspensionDays) || !suspensionDays ? (
                <Badge variant="danger" size="sm" shape="roundedRectangle">
                    <AlertCircleIcon />
                    {t('users:status_suspended')}
                </Badge>
            ) : (
                <Tooltip variant="light" content={t('projects:days_of_suspension', {count: suspensionDays})}>
                    <Badge variant="danger" size="sm" shape="roundedRectangle">
                        <AlertCircleIcon />
                        {t('users:status_suspended')}
                    </Badge>
                </Tooltip>
            )}

            {suspensionStartDate && !suspensionEndDate && (
                <Flexbox align="center" justify="center" gap={1}>
                    <StyledPoint />
                    <Text size="textSm" palette="neutral" shade="600">
                        <Trans
                            i18nKey="projects:suspension_start_date"
                            values={{date: dayjs(suspensionStartDate).format('DD/MM/YYYY')}}
                            components={{b: <b />}}
                        />
                        {suspensionReason ? ` - ${t(suspendReasonToLabel[suspensionReason])}` : null}
                    </Text>
                </Flexbox>
            )}

            {suspensionEndDate && (
                <Flexbox align="center" justify="center" gap={1}>
                    <StyledPoint />
                    <Text size="textSm" palette="neutral" shade="600">
                        <Trans
                            i18nKey="projects:suspension_end_date"
                            values={{date: dayjs(suspensionEndDate).format('DD/MM/YYYY')}}
                            components={{b: <b />}}
                        />
                        {suspensionReason ? ` - ${t(suspendReasonToLabel[suspensionReason])}` : null}
                    </Text>
                </Flexbox>
            )}
        </Flexbox>
    )
}
